import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useDropzone } from 'react-dropzone';
import { Capacitor } from '@capacitor/core';
import { collection, getDocs, query, limit } from 'firebase/firestore';
import {
  X, Check, Plus, ClipboardPaste, AlertTriangle, Loader2, Sparkles, Lock, ChevronRight, RotateCcw,
} from 'lucide-react';
import { auth, db } from '../../firebase';
import { apiUrl, postStreamedJson } from '../../lib/api';
import { track } from '../../lib/analytics';
import { cn } from '../../lib/utils';
import { celebrate, tapLight } from '../../lib/haptics';
import { reconcileGameImport, ReconciledGame } from '../../lib/pdf/reconcileGameImport';
import { computeGameSignals } from '../../lib/insights/game-signals';
import type { ParsedScorebook, ParsedGameStatsSheet } from '../../types/import.types';
import type { ParsedGCPlayer } from '../../lib/gcCsvParser';
import { readGcFile, readPastedCsv, ReadResult } from '../../lib/import/gc-read-file';
import {
  SourceKind, identifyGame, findGameMatch, pdfsDisagree, ourSideOfSheet, boundGameMismatch,
  suggestGameForCsv, matchCsvRows, checkCsvAgainstPdfs, pickPitchCounts, csvRunsScored,
  localDay, prettyDay, looksLikeGcCsv,
} from '../../lib/import/gc-sources';
import { saveGcImport, saveGameInsights, loadGameInsights, SaveGcImportResult, CsvLine } from '../../lib/import/gc-save';
import { loadImportSession, saveImportSession, clearImportSession } from '../../lib/import/gc-session-store';
import { clearShareLog } from '../../lib/share-import';
import { GameInsightCard, InsightCardData, groupInsightsByInning } from '../Insights/GameInsightCard';
import { RotatingStatus } from '../RotatingStatus';

/**
 * The one place GameChanger data comes into Dugout.
 *
 * Replaces three screens (the per-game "Import Game Data" hub, the PDF importer
 * and the per-game CSV importer) that each held part of the job, which is how a
 * coach could send every file and still find the game asking for stats.
 *
 * Every route lands here: the iOS share sheet ("Import to Dugout"), the file
 * picker, a CSV paste, drag and drop on the web, and every game card button.
 * Files arrive in any order and are identified by content. Each one is
 * acknowledged the moment it lands, cross-checked against the others, and the
 * coach saves once. Unsaved files survive an app restart (gc-session-store), so
 * hopping back to GameChanger for the next file can never lose the last one.
 */

interface Props {
  team: any;
  /** Active roster: no guests, no retired players. */
  players: any[];
  /** The schedule, for matching files to a game. */
  games: any[];
  /** Opened from a game card: the import bonds to this game. */
  game?: any;
  isElite: boolean;
  /** The subscription is still resolving: hold shared files, show no lock yet. */
  eliteLoading?: boolean;
  onUpgrade: () => void;
  onClose: () => void;
  onViewBoxScore?: (game: any) => void;
  /** Files handed over by the iOS share extension; a new nonce = a new batch. */
  incoming?: { files: File[]; nonce: number } | null;
}

interface Sources {
  book: { data: ParsedScorebook; fileName: string } | null;
  sheet: { data: ParsedGameStatsSheet; fileName: string } | null;
  csv: { rows: ParsedGCPlayer[]; fileName: string; via: 'file' | 'paste' } | null;
}

type TargetChoice = { kind: 'auto' } | { kind: 'game'; id: string } | { kind: 'new' };

interface Choices {
  overrides: Record<string, string>;   // scorebook "number::name" → playerId
  csvPick: Record<number, string>;     // CSV row → playerId ('' = leave out)
  csvTrusted: boolean;                 // coach vouched for a CSV that didn't line up
  target: TargetChoice;
  keepDugout: string[];                // pitchers whose live Dugout count wins
}

type Target =
  | { kind: 'existing'; game: any; how: 'bound' | 'picked' | 'exact' | 'likely' | 'suggested'; ambiguous?: boolean }
  | { kind: 'new'; opponent: string; day: string; isHome: boolean };

interface Receipt { id: number; kind: SourceKind | 'rejected'; replaced: boolean; message?: string }

const EMPTY: Sources = { book: null, sheet: null, csv: null };
const NO_CHOICES: Choices = { overrides: {}, csvPick: {}, csvTrusted: false, target: { kind: 'auto' }, keepDugout: [] };

const listJoin = (items: string[]) =>
  items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export function GameDataImport({ team, players, games, game, isElite, eliteLoading = false, onUpgrade, onClose, onViewBoxScore, incoming }: Props) {
  const sessionKey = `${team.id}:${game?.id || 'any'}`;
  const isNative = Capacitor.isNativePlatform();

  const [sources, setSources] = useState<Sources>(EMPTY);
  const [choices, setChoices] = useState<Choices>(NO_CHOICES);
  const [ready, setReady] = useState(false);
  const [restored, setRestored] = useState(false);
  const [reading, setReading] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteHint, setPasteHint] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  // Set at save. A game that already has insights offers to show them (a
  // Firestore read); one without offers to write them. Claude only runs when the
  // coach taps for it, never as part of the save, and never over existing cards.
  const [insightsOffer, setInsightsOffer] = useState<{ existing: boolean } | null>(null);
  const [phase, setPhase] = useState<'collect' | 'saving' | 'done' | 'error'>('collect');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [saved, setSaved] = useState<SaveGcImportResult | null>(null);
  const [savedLabel, setSavedLabel] = useState('');
  const [insights, setInsights] = useState<InsightCardData[] | null>(null);
  const [insightsFailed, setInsightsFailed] = useState(false);
  const [probe, setProbe] = useState<{ atBats: boolean; insights: boolean } | null>(null);

  // Refs so a batch of files (and a share arriving mid-read) always sees the
  // latest state, never the render it started in. The old importer's per-drop
  // closures are how a second PDF used to reconcile without the first.
  const sourcesRef = useRef(sources);
  sourcesRef.current = sources;
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const receiptSeq = useRef(0);
  const createdRef = useRef<any>(null);
  const savedReconRef = useRef<ReconciledGame | null>(null);

  // ── Restore an unsaved import, then accept shared files ─────────────────────
  useEffect(() => {
    let live = true;
    loadImportSession(sessionKey).then(data => {
      if (!live) return;
      const s: Sources | undefined = data?.sources;
      if (s && (s.book || s.sheet || s.csv)) {
        setSources(s);
        sourcesRef.current = s;
        setChoices({ ...NO_CHOICES, ...(data.choices || {}) });
        setRestored(true);
      }
      setReady(true);
    });
    return () => { live = false; };
  }, [sessionKey]);

  useEffect(() => {
    if (!ready || phase !== 'collect') return;
    if (sources.book || sources.sheet || sources.csv) void saveImportSession(sessionKey, { sources, choices });
    else void clearImportSession(sessionKey);
  }, [sources, choices, ready, phase, sessionKey]);

  const handledNonce = useRef(0);
  useEffect(() => {
    if (!ready || !incoming || incoming.nonce <= handledNonce.current) return;
    handledNonce.current = incoming.nonce;
    void addFiles(incoming.files);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, incoming?.nonce]);

  // ── Taking files in ─────────────────────────────────────────────────────────
  // `fromNewFiles`: the next game's files are arriving after a save. The share
  // checklist already holds them, so leave it alone.
  const startOver = (fromNewFiles = false) => {
    setSources(EMPTY);
    sourcesRef.current = EMPTY;
    setChoices(NO_CHOICES);
    setReceipt(null);
    setRestored(false);
    setSaved(null);
    setInsights(null);
    setInsightsFailed(false);
    setInsightsOffer(null);
    setError('');
    createdRef.current = null;
    setPhase('collect');
    void clearImportSession(sessionKey);
    if (!fromNewFiles) void clearShareLog();
  };

  const accept = (r: ReadResult) => {
    const cur = sourcesRef.current;
    let next = cur;
    let replaced = false;
    if (r.kind === 'scorebook') { replaced = !!cur.book; next = { ...cur, book: { data: r.book, fileName: r.fileName } }; }
    if (r.kind === 'boxScore') { replaced = !!cur.sheet; next = { ...cur, sheet: { data: r.sheet, fileName: r.fileName } }; }
    if (r.kind === 'csv') { replaced = !!cur.csv; next = { ...cur, csv: { rows: r.rows, fileName: r.fileName, via: r.via } }; }
    if (next !== cur) {
      sourcesRef.current = next;
      setSources(next);
      tapLight();
      // A replaced file voids the answers given about the old one.
      if (r.kind === 'csv') setChoices(c => ({ ...c, csvPick: {}, csvTrusted: false }));
      if (r.kind === 'scorebook') setChoices(c => ({ ...c, overrides: {} }));
    }
    setReceipt({
      id: ++receiptSeq.current,
      kind: r.kind === 'rejected' ? 'rejected' : r.kind,
      replaced,
      message: r.kind === 'rejected' ? r.message : undefined,
    });
  };

  // Files that arrived before Elite was confirmed wait here instead of being
  // dropped: a subscriber's plan may still be loading on a cold start, and a
  // coach who upgrades from the lock card gets their file brought straight in.
  const pendingRef = useRef<File[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  useEffect(() => {
    if (!isElite || !ready || !pendingRef.current.length) return;
    const files = pendingRef.current;
    pendingRef.current = [];
    setPendingCount(0);
    void addFiles(files);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isElite, ready]);

  const addFiles = async (files: File[]) => {
    if (!files.length) return;
    if (!isElite) {
      pendingRef.current = [...pendingRef.current, ...files];
      setPendingCount(pendingRef.current.length);
      return;
    }
    // Files arriving after a finished save are the next game: start clean.
    if (phaseRef.current === 'done') startOver(true);
    // After a failed save the files are still this game's: add to them.
    if (phaseRef.current === 'error') setPhase('collect');
    for (const f of files) {
      setReading(f.name || 'your file');
      accept(await readGcFile(f));
    }
    setReading(null);
  };

  const acceptPaste = (text: string) => {
    if (!isElite) { onUpgrade(); return; }
    const r = readPastedCsv(text);
    accept(r);
    if (r.kind === 'csv') { setPasteOpen(false); setPasteText(''); setPasteHint(''); }
  };

  const pasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text?.trim()) { setPasteText(text); acceptPaste(text); return; }
      setPasteHint('Your clipboard is empty. Copy the stats in GameChanger first.');
    } catch {
      setPasteHint('Tap and hold in the box below, then tap Paste.');
    }
  };

  const remove = (kind: SourceKind) => {
    const next = { ...sourcesRef.current, [kind === 'boxScore' ? 'sheet' : kind === 'scorebook' ? 'book' : 'csv']: null };
    sourcesRef.current = next;
    setSources(next);
    if (kind === 'csv') setChoices(c => ({ ...c, csvPick: {}, csvTrusted: false }));
    if (kind === 'scorebook') setChoices(c => ({ ...c, overrides: {} }));
    setReceipt(null);
  };

  const { getRootProps, getInputProps, open: openPicker, isDragActive } = useDropzone({
    onDrop: (f) => { void addFiles(f); },
    noClick: true,
    noKeyboard: true,
    multiple: true,
    // iOS greys out files it can't map to these types, so the native picker
    // takes anything and the content check sorts it out.
    accept: isNative ? undefined : { 'application/pdf': ['.pdf'], 'text/csv': ['.csv'], 'text/plain': ['.csv', '.txt'] },
  });

  // ── What the files say ──────────────────────────────────────────────────────
  const roster = useMemo(
    () => players.map((p: any) => ({ id: p.id, name: p.name, number: String(p.number ?? '') })),
    [players],
  );
  const nameOf = (id: string) => players.find((p: any) => p.id === id)?.name || 'Unknown player';
  const book = sources.book?.data ?? null;
  const sheet = sources.sheet?.data ?? null;
  const csvRows = sources.csv?.rows ?? null;
  const hasPdf = !!(book || sheet);
  const hasAny = !!(book || sheet || csvRows);

  const reconBase = useMemo(
    () => book ? reconcileGameImport({ scorebook: book, gameStatsSheet: sheet || undefined, coachTeamName: team.name, roster, overrides: choices.overrides }) : null,
    [book, sheet, team.name, roster, choices.overrides],
  );
  const sheetOurs = useMemo(() => sheet ? ourSideOfSheet(sheet, team.name, roster).ours : undefined, [sheet, team.name, roster]);
  const identity = useMemo(() => identifyGame(reconBase, sheet, team.name, roster), [reconBase, sheet, team.name, roster]);
  const pdfConflict = useMemo(() => pdfsDisagree(reconBase, sheet), [reconBase, sheet]);

  // Which game this all goes to.
  const autoMatch = useMemo(() => hasPdf ? findGameMatch(identity, games) : null, [hasPdf, identity, games]);
  const csvSuggestion = useMemo(
    () => (!hasPdf && csvRows && !game) ? suggestGameForCsv(csvRows, games) : null,
    [hasPdf, csvRows, game, games],
  );
  const boundMismatch = game && hasPdf ? boundGameMismatch(game, identity) : null;
  const canCreate = !!(identity.date && identity.opponent);

  const target: Target | null = (() => {
    const t = choices.target;
    if (t.kind === 'game') {
      const g = games.find((x: any) => x.id === t.id) ?? (game?.id === t.id ? game : null) ?? (createdRef.current?.id === t.id ? createdRef.current : null);
      return g ? { kind: 'existing', game: g, how: 'picked' } : null;
    }
    if (t.kind === 'new') return canCreate ? { kind: 'new', opponent: identity.opponent!, day: identity.date!, isHome: identity.homeOrAway === 'home' } : null;
    if (game) return { kind: 'existing', game, how: 'bound' };
    if (autoMatch) return { kind: 'existing', game: autoMatch.game, how: autoMatch.confidence, ambiguous: autoMatch.ambiguous };
    if (canCreate) return { kind: 'new', opponent: identity.opponent!, day: identity.date!, isHome: identity.homeOrAway === 'home' };
    if (csvSuggestion) return { kind: 'existing', game: csvSuggestion, how: 'suggested' };
    return null;
  })();
  const targetGame = target?.kind === 'existing' ? target.game : null;
  const needsBoundDecision = !!boundMismatch && choices.target.kind === 'auto';

  // The CSV: roster matching, then does it back up the PDFs?
  const csvMatchRows = useMemo(() => csvRows ? matchCsvRows(csvRows, roster) : [], [csvRows, roster]);
  const resolvedCsv = useMemo(() => csvMatchRows.map(r =>
    r.index in choices.csvPick
      ? (choices.csvPick[r.index] || null)
      : (r.match.confidence === 'unmatched' ? null : r.match.matchedPlayerId)),
  [csvMatchRows, choices.csvPick]);
  const knownFinal = identity.final ?? (targetGame?.result ? { us: targetGame.result.myScore, them: targetGame.result.opponentScore } : undefined);
  const csvCheck = useMemo(
    () => csvRows ? checkCsvAgainstPdfs(csvRows, resolvedCsv, sheetOurs, reconBase, knownFinal) : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [csvRows, resolvedCsv, sheetOurs, reconBase, knownFinal?.us],
  );
  const csvMismatch = csvCheck?.verdict === 'mismatch' && !choices.csvTrusted;
  const csvUsable = !!csvRows && !csvMismatch;

  // Full reconcile with the CSV folded in: its rates are what insights need.
  const recon = useMemo(
    () => (book && csvUsable) ? reconcileGameImport({ scorebook: book, gameStatsSheet: sheet || undefined, gameCsv: csvRows!, coachTeamName: team.name, roster, overrides: choices.overrides }) : reconBase,
    [book, sheet, csvUsable, csvRows, team.name, roster, choices.overrides, reconBase],
  );

  const pitchPicks = useMemo(
    () => pickPitchCounts(recon, sheetOurs, roster, csvUsable ? csvRows : null, resolvedCsv, nameOf),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recon, sheetOurs, roster, csvUsable, csvRows, resolvedCsv],
  );
  // Only a count Dugout tracked itself (no earlier import) is worth asking about.
  const pitchConflicts = targetGame?.pitchCounts && !targetGame.gcImport
    ? pitchPicks.filter(p => targetGame.pitchCounts[p.playerId] && targetGame.pitchCounts[p.playerId] !== p.count)
    : [];

  const scorebookUnmatched = reconBase?.unmatched ?? [];
  const csvUnsure = csvUsable ? csvMatchRows.filter(r => r.played && !r.sure) : [];

  // What the chosen game already has from earlier imports.
  useEffect(() => {
    setProbe(null);
    if (!targetGame?.id || targetGame.id === createdRef.current?.id) return;
    let live = true;
    (async () => {
      try {
        const base = `teams/${team.id}/games/${targetGame.id}`;
        const [ab, ins] = await Promise.all([
          getDocs(query(collection(db, `${base}/atBats`), limit(1))),
          getDocs(query(collection(db, `${base}/insights`), limit(1))),
        ]);
        if (live) setProbe({ atBats: !ab.empty, insights: !ins.empty });
      } catch { /* status just shows what the game doc says */ }
    })();
    return () => { live = false; };
  }, [targetGame?.id, team.id]);

  const have = targetGame ? {
    scorebook: !!(targetGame.gcImport?.scorebook || targetGame.importedFrom === 'scorebook' || probe?.atBats),
    boxScore: !!targetGame.gcImport?.boxScore,
    csv: !!(targetGame.gcImport?.csv || (targetGame.stats && Object.keys(targetGame.stats).length)),
    insights: !!(targetGame.gcImport?.insights || probe?.insights),
  } : null;
  const hasGameStats = !!(targetGame?.stats && Object.keys(targetGame.stats).length);

  const canInsights = isElite && !!recon && Object.keys(recon.pitcherRates).length > 0;

  // ── Can we save? ────────────────────────────────────────────────────────────
  const blocker: string | null =
    !hasAny ? 'Add a file to get started.'
    : reading ? 'Reading your file…'
    : pdfConflict ? 'Remove the PDF from the other game to continue.'
    : needsBoundDecision ? 'Choose which game these files go to.'
    : csvMismatch ? 'Decide what to do with the stats CSV above.'
    : !(book || sheet || csvUsable) ? 'Add a file to get started.'
    : !target ? 'Pick the game these stats belong to.'
    : null;
  const canSave = isElite && phase === 'collect' && !blocker;
  // Where the decision the coach owes us lives, so the footer can take them there.
  const decisionAnchor = pdfConflict ? 'gc-decision-pdf' : needsBoundDecision ? 'gc-decision-game' : csvMismatch ? 'gc-decision-csv' : null;

  // ── Save ────────────────────────────────────────────────────────────────────
  const gameLabel = (g: any) => `${g.isHome === false ? '@' : 'vs'} ${g.opponent || 'Opponent'} · ${prettyDay(localDay(g.date))}`;
  const targetLabel = target
    ? target.kind === 'existing'
      ? gameLabel(target.game)
      : `${target.isHome ? 'vs' : '@'} ${target.opponent} · ${prettyDay(target.day)}`
    : '';

  const generateInsights = async (r: ReconciledGame): Promise<InsightCardData[]> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = await auth.currentUser?.getIdToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const signals = computeGameSignals(r.atBats, r.pitcherLines, r.pitcherRates);
    const body = JSON.stringify({
      teamInfo: { name: team.name, ageGroup: team.ageGroup },
      game: { opponent: r.opponent, date: r.date, homeOrAway: r.homeOrAway },
      signals,
    });
    const data = await postStreamedJson(apiUrl('/api/game-insights'), headers, body, 'GameImport');
    if (data?.__httpError) throw new Error(data.error || 'Insight generation failed.');
    // A matched insight always shows the roster name, never GameChanger's
    // truncated one.
    const rosterById = new Map(players.map((p: any) => [p.id, p.name as string]));
    return (data.insights || []).map((ins: InsightCardData) =>
      ins.subjectPlayerId && rosterById.has(ins.subjectPlayerId)
        ? { ...ins, subjectPlayerName: rosterById.get(ins.subjectPlayerId)! }
        : ins);
  };

  const showSavedInsights = async (gameId: string) => {
    setBusy('Pulling up your insights…');
    try {
      setInsights(await loadGameInsights(team.id, gameId) as InsightCardData[]);
      setInsightsFailed(false);
    } catch {
      setInsightsFailed(true);
    }
  };

  const runInsights = async (gameId: string, r: ReconciledGame) => {
    setBusy('Writing your insights…');
    try {
      const cards = await generateInsights(r);
      await saveGameInsights(team.id, gameId, cards);
      setInsights(cards);
      setInsightsFailed(false);
      track('insights_generated', { count: cards.length, source: 'scorebook_pdf' });
    } catch {
      setInsightsFailed(true);
    }
  };

  const handleSave = async () => {
    if (!canSave || !target) return;
    setPhase('saving');
    setBusy('Saving your game…');
    setError('');
    try {
      const seen = new Set<string>();
      const lines: CsvLine[] = [];
      if (csvUsable && csvRows) {
        csvRows.forEach((row, i) => {
          const pid = resolvedCsv[i];
          if (!pid || !row.stats || seen.has(pid)) return;
          seen.add(pid);
          lines.push({ playerId: pid, stats: row.stats, fieldTime: row.fieldTime });
        });
      }
      const pitchCounts = Object.fromEntries(
        pitchPicks.filter(p => !choices.keepDugout.includes(p.playerId)).map(p => [p.playerId, p.count]),
      );
      // A retry after a failure part-way through must not add the game twice.
      const saveTarget = target.kind === 'new' && createdRef.current
        ? { kind: 'existing' as const, game: createdRef.current }
        : target.kind === 'existing' ? { kind: 'existing' as const, game: target.game } : target;
      const res = await saveGcImport({
        team, players,
        target: saveTarget,
        recon,
        final: identity.final,
        csv: lines.length ? { lines, fileName: sources.csv!.fileName, via: sources.csv!.via } : null,
        pitchCounts,
        files: { scorebook: sources.book?.fileName, boxScore: sources.sheet?.fileName },
        onGameCreated: (g) => { createdRef.current = g; },
      });
      setSaved(res);
      setSavedLabel(targetLabel);
      savedReconRef.current = recon;
      void clearImportSession(sessionKey);
      void clearShareLog();
      setInsights(null);
      setInsightsFailed(false);
      setInsightsOffer(have?.insights ? { existing: true } : canInsights ? { existing: false } : null);
      setPhase('done');
      celebrate();
    } catch (e: any) {
      console.error('GameChanger import save failed:', e);
      setError('Something went wrong saving this game. Your files are still here. Check your connection and try again.');
      setPhase('error');
    }
  };

  // ── Copy for the live receipt banner ────────────────────────────────────────
  const ourPAs = reconBase ? reconBase.atBats.filter(a => a.batterTeam === 'us').length : 0;
  const sheetPitchers = sheetOurs?.pitchers.filter(p => p.pitches != null).length ?? 0;
  const finalText = identity.final ? `${identity.final.us}–${identity.final.them}` : '';
  const csvPlayed = csvMatchRows.filter(r => r.played).length;

  const csvCheckLine = (): { tone: 'good' | 'warn' | 'info'; text: string } | null => {
    if (!csvCheck) return null;
    const { basis, verdict, agreed, compared, off } = csvCheck;
    if (verdict === 'unchecked') return { tone: 'info', text: hasPdf ? 'Nothing on these PDFs to compare it with.' : 'Add a PDF and Dugout will cross-check it.' };
    if (verdict === 'match') {
      if (basis === 'boxScore') return { tone: 'good', text: 'Lines up with the game stats PDF, player for player.' };
      if (basis === 'scorebook') return { tone: 'good', text: 'Backs up the scorebook, player for player.' };
      return { tone: 'good', text: `Its runs match the final score (${knownFinal?.us}).` };
    }
    if (verdict === 'mostly') return { tone: 'info', text: `Lines up for ${agreed} of ${compared}. Worth a look: ${listJoin(off)}.` };
    if (choices.csvTrusted) return { tone: 'info', text: 'Using it, as you asked, even though it didn’t line up.' };
    if (basis === 'finalScore') return { tone: 'warn', text: `Its runs (${csvRunsScored(csvRows!)}) don't match this game's final score (${knownFinal?.us}). It may be a different game.` };
    return { tone: 'warn', text: "It doesn't line up with these PDFs. It may be from a different game." };
  };

  const receiptText = (r: Receipt): { tone: 'good' | 'warn'; text: string } => {
    if (r.kind === 'rejected') return { tone: 'warn', text: r.message || "That file couldn't be used." };
    const swap = r.replaced ? 'Swapped in the new one. ' : '';
    if (r.kind === 'scorebook') {
      return { tone: 'good', text: `${swap}Got your scorebook PDF${identity.opponent ? ` vs ${identity.opponent}` : ''}. ${plural(ourPAs, 'plate appearance')}, pitch by pitch.` };
    }
    if (r.kind === 'boxScore') {
      if (!sheetOurs) return { tone: 'warn', text: "Got a game stats PDF, but your team isn't on it. Check your roster's jersey numbers." };
      return { tone: 'good', text: `${swap}Got your game stats PDF. ${finalText ? `Final ${finalText}, ` : ''}exact pitch counts for ${plural(sheetPitchers, 'pitcher')}.` };
    }
    const line = csvCheckLine();
    const base = `${swap}Got your stats CSV. One game, ${plural(csvPlayed, 'player')}.`;
    if (line?.tone === 'warn') return { tone: 'warn', text: `${base} ${line.text}` };
    return { tone: 'good', text: line && line.tone === 'good' ? `${base} ${line.text}` : base };
  };

  const missing = [!book && 'scorebook PDF', !sheet && 'game stats PDF', !csvRows && 'stats CSV'].filter(Boolean) as string[];
  const nextHint = !hasAny
    ? 'Send any of the three, in any order. Dugout figures out which is which.'
    : missing.length === 0
      ? "That's everything. Save it and you're done."
      : `Next: the ${listJoin(missing)}. Or save now with what you have.`;

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="fixed inset-0 z-[110] bg-canvas safe-top"
    >
    <div {...getRootProps({ className: 'h-full flex flex-col relative outline-none' })}>
      <input {...getInputProps()} aria-label="Choose GameChanger files" />

      {/* Header */}
      <div className="bg-surface border-b border-hairline px-4 py-3 flex items-center gap-3">
        <button onClick={onClose} aria-label="Close" className="p-1.5 -ml-1.5 text-ink-2 hover:text-ink">
          <X className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h2 className="text-[17px] font-semibold text-ink truncate">Import from GameChanger</h2>
          <p className="text-[12px] font-medium text-ink-2 truncate">
            {phase === 'done' ? savedLabel : (targetLabel || 'Scorebook, game stats and stats CSV')}
          </p>
        </div>
      </div>

      {isDragActive && (
        <div className="absolute inset-0 z-10 bg-surface/90 flex items-center justify-center pointer-events-none">
          <p className="text-lg font-semibold text-ink">Drop to add</p>
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-lg mx-auto px-4 pt-4 pb-8 space-y-5">

          {phase === 'saving' && (
            <div className="flex flex-col items-center text-center py-16 space-y-3">
              <Loader2 className="w-8 h-8 text-accent-ink animate-spin" />
              <p className="text-base font-semibold text-ink">{busy}</p>
              {busy.startsWith('Writing') ? <RotatingStatus /> : <p className="text-sm text-ink-2">One moment</p>}
            </div>
          )}

          {phase === 'error' && (
            <div className="bg-surface border border-hairline rounded-card p-5 space-y-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-danger shrink-0 mt-0.5" />
                <p className="text-sm text-ink">{error}</p>
              </div>
              <button onClick={() => setPhase('collect')} className="w-full py-3 rounded-control bg-ink text-surface text-sm font-semibold">
                Back to my files
              </button>
            </div>
          )}

          {phase === 'done' && saved && (
            <DoneView
              saved={saved}
              insights={insights}
              insightsFailed={insightsFailed}
              insightsOffer={insightsOffer}
              noCsv={!saved.boxScorePlayers}
              onInsights={async () => {
                if (insightsOffer?.existing) {
                  setPhase('saving');
                  await showSavedInsights(saved.gameId);
                  setPhase('done');
                  return;
                }
                if (!savedReconRef.current) return;
                setPhase('saving');
                await runInsights(saved.gameId, savedReconRef.current);
                setPhase('done');
              }}
              onViewBoxScore={onViewBoxScore && saved.boxScorePlayers
                ? () => onViewBoxScore(games.find((g: any) => g.id === saved.gameId) ?? { ...(targetGame || createdRef.current || {}), id: saved.gameId })
                : undefined}
              onDone={onClose}
            />
          )}

          {phase === 'collect' && (
            <>
              {restored && hasAny && (
                <div className="flex items-center gap-3 text-[13px] text-ink-2">
                  <RotateCcw className="w-4 h-4 shrink-0" />
                  <span className="flex-1">Picked up where you left off.</span>
                  <button onClick={() => startOver()} className="font-semibold text-ink underline underline-offset-2">Start over</button>
                </div>
              )}

              {/* The live receipt: every file is acknowledged the moment it lands. */}
              <AnimatePresence mode="wait">
                {reading ? (
                  <motion.div key="reading" initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    className="bg-surface border border-hairline rounded-card px-4 py-3 flex items-center gap-3">
                    <Loader2 className="w-4 h-4 text-ink-2 animate-spin shrink-0" />
                    <p className="text-sm font-medium text-ink truncate">Reading {reading}…</p>
                  </motion.div>
                ) : receipt ? (() => {
                  const r = receiptText(receipt);
                  return (
                    <motion.div key={receipt.id} initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                      role="status"
                      className={cn('rounded-card px-4 py-3 flex items-start gap-3 border',
                        r.tone === 'good' ? 'bg-surface border-accent' : 'bg-surface border-danger-fill')}>
                      {r.tone === 'good'
                        ? <span className="w-5 h-5 rounded-full bg-accent flex items-center justify-center shrink-0 mt-px"><Check className="w-3 h-3 text-surface" strokeWidth={3} /></span>
                        : <AlertTriangle className="w-5 h-5 text-danger shrink-0 mt-px" />}
                      <p className="text-sm font-medium text-ink leading-snug">{r.text}</p>
                    </motion.div>
                  );
                })() : null}
              </AnimatePresence>

              {isNative && !hasAny && isElite && (
                <div className="bg-surface border border-hairline rounded-card px-4 py-3">
                  <p className="text-sm font-semibold text-ink">Fastest from your iPhone</p>
                  <p className="text-[13px] text-ink-2 mt-1 leading-snug">
                    In GameChanger, open each export, tap Share, then <span className="font-semibold text-ink">Import to Dugout</span>. Each file lands right here, one at a time.
                  </p>
                </div>
              )}

              {/* The three sources */}
              <section className="space-y-2">
                <SectionLabel>Your files</SectionLabel>
                <div className="bg-surface border border-hairline rounded-card divide-y divide-hairline">
                  <SourceRow
                    title="Scorebook PDF"
                    state={book ? 'received' : have?.scorebook ? 'inDugout' : 'empty'}
                    detail={book
                      ? `${reconBase?.ourTeamName || 'Your team'} ${identity.homeOrAway === 'home' ? 'vs' : '@'} ${identity.opponent || '?'} · ${prettyDay(identity.date)} · ${plural(ourPAs, 'plate appearance')}`
                      : have?.scorebook ? 'Already in Dugout. Send a new one to replace it.'
                      : 'Play-by-play, pitch by pitch. In GameChanger: the game, Plays tab, share the scorebook.'}
                    fileName={sources.book?.fileName}
                    onRemove={book ? () => remove('scorebook') : undefined}
                  />
                  <SourceRow
                    title="Game stats PDF"
                    state={sheet ? (sheetOurs ? 'received' : 'problem') : have?.boxScore ? 'inDugout' : 'empty'}
                    detail={sheet
                      ? sheetOurs
                        ? `${finalText ? `Final ${finalText} · ` : ''}exact pitch counts for ${plural(sheetPitchers, 'pitcher')}`
                        : "Your team isn't on this one. Check your roster's jersey numbers."
                      : have?.boxScore ? 'Already in Dugout. Send a new one to replace it.'
                      : 'The box score: final score and exact pitch counts. In GameChanger: the game, Box Score tab, Export Stats.'}
                    fileName={sources.sheet?.fileName}
                    onRemove={sheet ? () => remove('boxScore') : undefined}
                  />
                  <SourceRow
                    title="Stats CSV"
                    state={csvRows ? (csvMismatch ? 'problem' : 'received') : have?.csv ? 'inDugout' : 'empty'}
                    detail={csvRows
                      ? `One game · ${plural(csvPlayed, 'player')}${sources.csv?.via === 'paste' ? ' · pasted' : ''}`
                      : have?.csv ? 'Already in Dugout. Send a new one to replace it.'
                      : 'Full stat lines and field time. In GameChanger: Stats, pick this game, Export. Upload the file or paste it.'}
                    fileName={sources.csv?.via === 'file' ? sources.csv.fileName : undefined}
                    onRemove={csvRows ? () => remove('csv') : undefined}
                    extra={(() => {
                      if (csvRows) {
                        const line = csvCheckLine();
                        return (
                          <>
                            {line && (
                              <p className={cn('text-[13px] mt-1 leading-snug font-medium',
                                line.tone === 'good' ? 'text-accent-ink' : line.tone === 'warn' ? 'text-danger' : 'text-ink-2')}>
                                {line.text}
                              </p>
                            )}
                            {csvMismatch && (
                              <div id="gc-decision-csv" className="flex gap-2 mt-2 scroll-mt-24">
                                <button onClick={() => remove('csv')} className="px-3 py-1.5 rounded-control border border-hairline text-[13px] font-semibold text-ink">Remove it</button>
                                <button onClick={() => setChoices(c => ({ ...c, csvTrusted: true }))} className="px-3 py-1.5 rounded-control border border-hairline text-[13px] font-semibold text-ink-2">It's the right game</button>
                              </div>
                            )}
                          </>
                        );
                      }
                      if (have?.csv && hasGameStats && onViewBoxScore && targetGame) {
                        return <button onClick={() => onViewBoxScore(targetGame)} className="text-[13px] font-semibold text-ink underline underline-offset-2 mt-1">View box score</button>;
                      }
                      return null;
                    })()}
                  />
                </div>

                {/* Add */}
                {isElite ? (
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <button onClick={openPicker} className="flex items-center justify-center gap-2 py-3 rounded-control bg-ink text-surface text-sm font-semibold">
                      <Plus className="w-4 h-4" /> Add files
                    </button>
                    <button onClick={() => setPasteOpen(o => !o)} className="flex items-center justify-center gap-2 py-3 rounded-control bg-surface border border-hairline text-ink text-sm font-semibold">
                      <ClipboardPaste className="w-4 h-4" /> Paste stats
                    </button>
                  </div>
                ) : eliteLoading ? (
                  <div className="bg-surface border border-hairline rounded-card p-4 flex items-center gap-3">
                    <Loader2 className="w-4 h-4 text-ink-2 animate-spin shrink-0" />
                    <p className="text-[13px] text-ink-2 flex-1">
                      {pendingCount ? `Got ${plural(pendingCount, 'file')}. Checking your plan, one moment.` : 'Checking your plan, one moment.'}
                    </p>
                  </div>
                ) : (
                  <div className="bg-surface border border-hairline rounded-card p-4 flex items-center gap-3">
                    <Lock className="w-4 h-4 text-ink-2 shrink-0" />
                    <p className="text-[13px] text-ink-2 flex-1">
                      {pendingCount
                        ? `${plural(pendingCount, 'file')} waiting. GameChanger imports are part of Elite; start it and ${pendingCount === 1 ? 'it comes' : 'they come'} straight in.`
                        : 'GameChanger imports are part of Elite.'}
                    </p>
                    <button onClick={onUpgrade} className="px-3 py-1.5 rounded-control bg-ink text-surface text-[13px] font-semibold shrink-0">See Elite</button>
                  </div>
                )}
                {!isNative && isElite && <p className="text-[12px] text-ink-2 text-center">Or drag files anywhere on this screen.</p>}

                {pasteOpen && isElite && (
                  <div className="bg-surface border border-hairline rounded-card p-4 space-y-3">
                    <p className="text-[13px] text-ink-2 leading-snug">
                      In GameChanger: Stats, pick this one game, Export, then Copy to Clipboard. Paste it here.
                    </p>
                    <button onClick={pasteFromClipboard} className="w-full py-2.5 rounded-control bg-ink text-surface text-sm font-semibold flex items-center justify-center gap-2">
                      <ClipboardPaste className="w-4 h-4" /> Paste from clipboard
                    </button>
                    {pasteHint && <p className="text-[12px] text-ink-2">{pasteHint}</p>}
                    <textarea
                      value={pasteText}
                      onChange={e => {
                        const v = e.target.value;
                        setPasteText(v);
                        // A paste lands all at once; take it as soon as it's whole.
                        if (looksLikeGcCsv(v) && v.split('\n').length > 2) acceptPaste(v);
                      }}
                      placeholder="Or paste the stats here"
                      className="w-full h-24 bg-canvas border border-hairline rounded-control p-3 text-[12px] text-ink placeholder:text-ink-2 resize-none focus:outline-none focus:border-ink-mute"
                    />
                    {pasteText.trim() && (
                      <button onClick={() => acceptPaste(pasteText)} className="w-full py-2.5 rounded-control border border-hairline text-sm font-semibold text-ink">
                        Use these stats
                      </button>
                    )}
                  </div>
                )}

                <p className="text-[13px] text-ink-2 px-1 pt-1">{nextHint}</p>
              </section>

              {/* Two PDFs from different games */}
              {pdfConflict && (
                <div id="gc-decision-pdf" className="bg-surface border border-danger-fill rounded-card p-4 space-y-3 scroll-mt-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-danger shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-ink">These PDFs are from different games</p>
                      <p className="text-[13px] text-ink-2 mt-1 leading-snug">{pdfConflict} Remove the one that doesn't belong.</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => remove('scorebook')} className="flex-1 py-2 rounded-control border border-hairline text-[13px] font-semibold text-ink">Remove scorebook</button>
                    <button onClick={() => remove('boxScore')} className="flex-1 py-2 rounded-control border border-hairline text-[13px] font-semibold text-ink">Remove game stats</button>
                  </div>
                </div>
              )}

              {/* The game */}
              {(hasAny || needsBoundDecision) && (
                <section className="space-y-2">
                  <SectionLabel>The game</SectionLabel>
                  {needsBoundDecision ? (
                    <div id="gc-decision-game" className="bg-surface border border-danger-fill rounded-card p-4 space-y-3 scroll-mt-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-danger shrink-0 mt-0.5" />
                        <p className="text-[13px] text-ink leading-snug">{boundMismatch}</p>
                      </div>
                      <div className="flex flex-col gap-2">
                        <button
                          onClick={() => setChoices(c => ({ ...c, target: autoMatch ? { kind: 'game', id: autoMatch.game.id } : canCreate ? { kind: 'new' } : c.target }))}
                          className="py-2.5 rounded-control bg-ink text-surface text-[13px] font-semibold">
                          {autoMatch
                            ? `Use the ${autoMatch.game.opponent || 'matching'} game (${prettyDay(localDay(autoMatch.game.date))})`
                            : canCreate ? `Add the ${identity.opponent} game as a new game` : 'Use the game in the files'}
                        </button>
                        <button onClick={() => setChoices(c => ({ ...c, target: { kind: 'game', id: game.id } }))}
                          className="py-2.5 rounded-control border border-hairline text-[13px] font-semibold text-ink">
                          Save to the {game.opponent || 'current'} game anyway
                        </button>
                      </div>
                    </div>
                  ) : target ? (
                    <button onClick={() => setPickerOpen(true)} className="w-full text-left bg-surface border border-hairline rounded-card px-4 py-3 flex items-center gap-3">
                      <span className={cn('w-5 h-5 rounded-full flex items-center justify-center shrink-0',
                        target.kind === 'existing' && (target.how === 'exact' || target.how === 'bound' || target.how === 'picked') ? 'bg-accent' : 'bg-raised border border-hairline')}>
                        {target.kind === 'existing' && (target.how === 'exact' || target.how === 'bound' || target.how === 'picked')
                          ? <Check className="w-3 h-3 text-surface" strokeWidth={3} />
                          : <Plus className="w-3 h-3 text-ink-2" />}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-ink leading-snug">
                          {target.kind === 'new'
                            ? `${target.isHome ? 'vs' : '@'} ${target.opponent}`
                            : `${target.game.isHome === false ? '@' : 'vs'} ${target.game.opponent || 'Opponent'}`}
                          <span className="font-medium text-ink-2"> · {target.kind === 'new' ? prettyDay(target.day) : prettyDay(localDay(target.game.date))}</span>
                        </p>
                        <p className="text-[12px] text-ink-2 mt-0.5 leading-snug">
                          {target.kind === 'new'
                            ? "New game. It isn't on your schedule yet, so saving adds it."
                            : target.how === 'suggested'
                              ? (target.game.result?.myScore === (csvRows ? csvRunsScored(csvRows) : -1)
                                ? 'Your most recent game without stats, and the runs match its score.'
                                : 'Your most recent game without stats. Tap to pick a different one.')
                              : target.ambiguous
                                ? 'You played them twice that day. Tap to check this is the right one.'
                                : target.how === 'likely'
                                  ? 'Looks like this one: the closest match on your schedule. Tap to change.'
                                  : target.game.result
                                    ? `On your schedule · ${target.game.result.outcome} ${target.game.result.myScore}–${target.game.result.opponentScore}`
                                    : 'On your schedule'}
                        </p>
                      </div>
                      <span className="text-[13px] font-semibold text-ink-2 flex items-center">Change<ChevronRight className="w-4 h-4" /></span>
                    </button>
                  ) : (
                    <button onClick={() => setPickerOpen(true)} className="w-full py-3 rounded-control border border-hairline bg-surface text-sm font-semibold text-ink">
                      Pick the game for these stats
                    </button>
                  )}
                  {target?.kind === 'existing' && identity.final && target.game.result
                    && (target.game.result.myScore !== identity.final.us || target.game.result.opponentScore !== identity.final.them) && (
                    <p className="text-[12px] text-ink-2 px-1">
                      GameChanger has the final at {finalText}; Dugout has {target.game.result.myScore}–{target.game.result.opponentScore}. Saving uses GameChanger's.
                    </p>
                  )}
                </section>
              )}

              {/* Names that didn't match the roster for sure */}
              {(scorebookUnmatched.length > 0 || csvUnsure.length > 0) && (
                <section className="space-y-2">
                  <SectionLabel>Match these names</SectionLabel>
                  <div className="bg-surface border border-hairline rounded-card divide-y divide-hairline">
                    {csvUnsure.map(r => (
                      <NameRow key={`csv-${r.index}`} label={`#${r.number} ${r.name}`} source="Stats CSV"
                        value={resolvedCsv[r.index] || ''}
                        players={players}
                        onChange={(pid) => setChoices(c => ({ ...c, csvPick: { ...c.csvPick, [r.index]: pid } }))} />
                    ))}
                    {scorebookUnmatched.map((u, i) => {
                      const key = `${u.number}::${u.name}`;
                      return (
                        <NameRow key={`sb-${i}`} label={`#${u.number} ${u.name}`} source={u.role === 'pitcher' ? 'Scorebook, pitching' : 'Scorebook'}
                          value={choices.overrides[key] || ''}
                          players={players}
                          onChange={(pid) => setChoices(c => {
                            const o = { ...c.overrides };
                            if (pid) o[key] = pid; else delete o[key];
                            return { ...c, overrides: o };
                          })} />
                      );
                    })}
                  </div>
                  <p className="text-[12px] text-ink-2 px-1">Anyone left out is still kept in the play-by-play; their stats just don't attach to a player.</p>
                </section>
              )}

              {/* Pitch counts that differ from what Dugout tracked live */}
              {pitchConflicts.length > 0 && (
                <section className="space-y-2">
                  <SectionLabel>Pitch counts</SectionLabel>
                  <div className="bg-surface border border-hairline rounded-card divide-y divide-hairline">
                    {pitchConflicts.map(p => {
                      const keep = choices.keepDugout.includes(p.playerId);
                      const set = (k: boolean) => setChoices(c => ({
                        ...c, keepDugout: k ? [...c.keepDugout.filter(x => x !== p.playerId), p.playerId] : c.keepDugout.filter(x => x !== p.playerId),
                      }));
                      return (
                        <div key={p.playerId} className="px-4 py-3 space-y-2">
                          <p className="text-sm font-semibold text-ink">{p.name}</p>
                          <div className="grid grid-cols-2 gap-2">
                            <ChoiceChip on={!keep} onClick={() => set(false)} label="GameChanger" value={`${p.count} pitches`} />
                            <ChoiceChip on={keep} onClick={() => set(true)} label="Dugout live" value={`${targetGame.pitchCounts[p.playerId]} pitches`} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-[12px] text-ink-2 px-1">The count you pick sets this pitcher's rest days.</p>
                </section>
              )}
            </>
          )}
        </div>
      </div>

      {/* Footer: one save */}
      {phase === 'collect' && (
        <div className="bg-surface border-t border-hairline px-4 pt-3 pb-3 safe-bottom">
          <div className="max-w-lg mx-auto space-y-2">
            {isElite && book && !csvUsable && (
              <p className="text-[12px] text-ink-2">Add the stats CSV to unlock AI insights for this game.</p>
            )}
            <button
              onClick={handleSave}
              disabled={!canSave}
              className={cn('w-full py-3.5 rounded-control text-[15px] font-semibold transition-colors',
                canSave ? 'bg-ink text-surface' : 'bg-raised text-ink-2 cursor-not-allowed')}
            >
              Save to Dugout
            </button>
            {blocker && hasAny && (
              decisionAnchor
                ? <button onClick={() => document.getElementById(decisionAnchor)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                    className="w-full text-[12px] font-semibold text-ink underline underline-offset-2 text-center">{blocker}</button>
                : <p className="text-[12px] text-ink-2 text-center">{blocker}</p>
            )}
          </div>
        </div>
      )}

      {/* Game picker */}
      <AnimatePresence>
        {pickerOpen && (
          <GamePicker
            games={games}
            aroundDay={identity.date}
            selectedId={targetGame?.id}
            newLabel={canCreate ? `Add ${identity.homeOrAway === 'home' ? 'vs' : '@'} ${identity.opponent} · ${prettyDay(identity.date)} as a new game` : null}
            gameLabel={gameLabel}
            onPick={(id) => { setChoices(c => ({ ...c, target: id ? { kind: 'game', id } : { kind: 'new' } })); setPickerOpen(false); }}
            onClose={() => setPickerOpen(false)}
          />
        )}
      </AnimatePresence>
    </div>
    </motion.div>
  );
}

// ── Pieces ──────────────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-2 px-1">{children}</p>;
}

function SourceRow({ title, state, detail, fileName, onRemove, extra }: {
  title: string;
  state: 'empty' | 'received' | 'inDugout' | 'problem';
  detail: string;
  fileName?: string;
  onRemove?: () => void;
  extra?: React.ReactNode;
}) {
  return (
    <div className="px-4 py-3 flex items-start gap-3">
      <span className={cn('w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-px',
        state === 'received' ? 'bg-accent'
          : state === 'inDugout' ? 'bg-raised border border-hairline'
          : state === 'problem' ? 'bg-surface border border-danger-fill'
          : 'border-2 border-dashed border-ink-mute')}>
        {state === 'received' && <Check className="w-3.5 h-3.5 text-surface" strokeWidth={3} />}
        {state === 'inDugout' && <Check className="w-3.5 h-3.5 text-accent-ink" strokeWidth={3} />}
        {state === 'problem' && <AlertTriangle className="w-3.5 h-3.5 text-danger" />}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <p className={cn('text-[15px] font-semibold', state === 'empty' ? 'text-ink-2' : 'text-ink')}>{title}</p>
          {state === 'inDugout' && <span className="text-[11px] font-semibold text-accent-ink">In Dugout</span>}
          {onRemove && <button onClick={onRemove} className="ml-auto text-[12px] font-semibold text-ink-2 hover:text-ink shrink-0">Remove</button>}
        </div>
        <p className="text-[13px] text-ink-2 mt-0.5 leading-snug">{detail}</p>
        {fileName && <p className="text-[11px] text-ink-2 mt-0.5 truncate">{fileName}</p>}
        {extra}
      </div>
    </div>
  );
}

function NameRow({ label, source, value, players, onChange }: {
  label: string; source: string; value: string; players: any[]; onChange: (pid: string) => void;
}) {
  return (
    <div className="px-4 py-3 flex items-center gap-3">
      <div className="w-32 shrink-0 min-w-0">
        <p className="text-[13px] font-semibold text-ink truncate">{label}</p>
        <p className="text-[11px] text-ink-2">{source}</p>
      </div>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="flex-1 min-w-0 bg-canvas border border-hairline rounded-control px-2 py-2 text-[13px] text-ink"
      >
        <option value="">Leave out</option>
        {players.map((p: any) => <option key={p.id} value={p.id}>#{p.number} {p.name}</option>)}
      </select>
    </div>
  );
}

function ChoiceChip({ on, onClick, label, value }: { on: boolean; onClick: () => void; label: string; value: string }) {
  return (
    <button onClick={onClick} className={cn('py-2 px-3 rounded-control text-left border transition-colors',
      on ? 'bg-ink text-surface border-ink' : 'bg-canvas text-ink border-hairline')}>
      <p className={cn('text-[11px] font-semibold', on ? 'text-surface' : 'text-ink-2')}>{on ? `Using ${label}` : `Use ${label}`}</p>
      <p className="text-[15px] font-semibold tabular-nums">{value}</p>
    </button>
  );
}

function GamePicker({ games, aroundDay, selectedId, newLabel, gameLabel, onPick, onClose }: {
  games: any[];
  aroundDay?: string;
  selectedId?: string;
  newLabel: string | null;
  gameLabel: (g: any) => string;
  onPick: (id: string | null) => void;
  onClose: () => void;
}) {
  const list = useMemo(() => {
    const now = Date.now();
    const pool = games.filter((g: any) => g && !g.isPractice && g.date);
    const anchor = aroundDay ? Date.parse(`${aroundDay}T12:00:00`) : now;
    return pool
      .filter((g: any) => aroundDay || Date.parse(g.date) <= now + 86400000)
      .sort((a: any, b: any) => Math.abs(Date.parse(a.date) - anchor) - Math.abs(Date.parse(b.date) - anchor))
      .slice(0, 25);
  }, [games, aroundDay]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[115] bg-ink/40 flex items-end sm:items-center justify-center" onClick={onClose}>
      <motion.div initial={{ y: 40 }} animate={{ y: 0 }} exit={{ y: 40 }}
        onClick={e => e.stopPropagation()}
        className="bg-surface w-full sm:max-w-md rounded-t-panel sm:rounded-panel max-h-[80vh] flex flex-col safe-bottom">
        <div className="px-4 py-3 border-b border-hairline flex items-center">
          <p className="text-[15px] font-semibold text-ink flex-1">Which game is this?</p>
          <button onClick={onClose} aria-label="Close" className="p-1 text-ink-2"><X className="w-5 h-5" /></button>
        </div>
        <div className="overflow-y-auto divide-y divide-hairline">
          {newLabel && (
            <button onClick={() => onPick(null)} className="w-full text-left px-4 py-3 flex items-center gap-3">
              <Plus className="w-4 h-4 text-ink-2 shrink-0" />
              <span className="text-sm font-semibold text-ink">{newLabel}</span>
            </button>
          )}
          {list.map((g: any) => (
            <button key={g.id} onClick={() => onPick(g.id)} className="w-full text-left px-4 py-3 flex items-center gap-3">
              <span className={cn('w-4 h-4 rounded-full border shrink-0', g.id === selectedId ? 'bg-accent border-accent' : 'border-ink-mute')} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-ink truncate">{gameLabel(g)}</p>
                <p className="text-[12px] text-ink-2">
                  {g.result ? `${g.result.outcome} ${g.result.myScore}–${g.result.opponentScore}` : g.status === 'completed' ? 'Final' : 'No score yet'}
                  {(g.gcImport || (g.stats && Object.keys(g.stats).length)) ? ' · has GameChanger data' : ''}
                </p>
              </div>
            </button>
          ))}
          {!list.length && <p className="px-4 py-6 text-sm text-ink-2">No games on your schedule yet.</p>}
        </div>
      </motion.div>
    </motion.div>
  );
}

function DoneView({ saved, insights, insightsFailed, insightsOffer, noCsv, onInsights, onViewBoxScore, onDone }: {
  saved: SaveGcImportResult;
  insights: InsightCardData[] | null;
  insightsFailed: boolean;
  insightsOffer: { existing: boolean } | null;
  noCsv: boolean;
  onInsights: () => void;
  onViewBoxScore?: () => void;
  onDone: () => void;
}) {
  const items: Array<{ label: string; sub: string }> = [];
  if (saved.createdGame) items.push({ label: 'Added to your schedule', sub: 'It was not there yet, so Dugout added it.' });
  if (saved.finalScore) items.push({ label: 'Final score', sub: `${saved.finalScore.outcome} ${saved.finalScore.us}–${saved.finalScore.them}${saved.finalScore.changedFrom ? ` (was ${saved.finalScore.changedFrom})` : ''}` });
  if (saved.plateAppearances) items.push({ label: 'Play-by-play', sub: `${plural(saved.plateAppearances, 'plate appearance')}, pitch by pitch` });
  if (saved.boxScorePlayers) items.push({ label: 'Box score', sub: `${plural(saved.boxScorePlayers, 'player')}, season stats updated` });
  if (saved.fieldTimePlayers) items.push({ label: 'Field time', sub: `${plural(saved.fieldTimePlayers, 'player')}, counted toward fair playing time` });
  if (saved.pitchers) items.push({ label: 'Pitch counts', sub: `${plural(saved.pitchers, 'pitcher')}${saved.restDaysUpdated ? ', rest days updated' : ''}` });
  if (insights) items.push({ label: 'AI insights', sub: insights.length ? `${plural(insights.length, 'insight')} for this game, below` : insightsOffer?.existing ? 'None saved for this game' : 'Nothing stood out enough to call out this time' });

  return (
    <div className="space-y-5">
      <div className="flex flex-col items-center text-center pt-6 space-y-3">
        <span className="w-16 h-16 rounded-full bg-accent flex items-center justify-center">
          <Check className="w-8 h-8 text-surface" strokeWidth={3} />
        </span>
        <h3 className="text-2xl font-semibold text-ink">You're all set, coach.</h3>
        <p className="text-sm text-ink-2">Here's what's in Dugout now.</p>
      </div>

      <div className="bg-surface border border-hairline rounded-card divide-y divide-hairline">
        {items.map(it => (
          <div key={it.label} className="px-4 py-3 flex items-start gap-3">
            <span className="w-5 h-5 rounded-full bg-accent flex items-center justify-center shrink-0 mt-px">
              <Check className="w-3 h-3 text-surface" strokeWidth={3} />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-ink">{it.label}</p>
              <p className="text-[13px] text-ink-2">{it.sub}</p>
            </div>
          </div>
        ))}
      </div>

      {saved.statusLeftLive && (
        <p className="text-[13px] text-ink-2 px-1">This game is still live in Dugout, so its final score waits for End Game.</p>
      )}
      {noCsv && (
        <p className="text-[13px] text-ink-2 px-1">Box score stats and field time come from the stats CSV. Send it any time and it joins this game.</p>
      )}
      {insightsOffer && !insights && (
        insightsFailed ? (
          <div className="bg-surface border border-hairline rounded-card p-4 space-y-3">
            <p className="text-[13px] text-ink">The insights didn't come through this time. Your game is saved.</p>
            <button onClick={onInsights} className="w-full py-2.5 rounded-control border border-hairline text-sm font-semibold text-ink">Try the insights again</button>
          </div>
        ) : insightsOffer.existing ? (
          <div className="bg-surface border border-hairline rounded-card p-4 space-y-3">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-accent-ink shrink-0 mt-px" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-ink">This game already has insights</p>
                <p className="text-[13px] text-ink-2">They're saved with the game. You can find them any time from its card on the Games tab.</p>
              </div>
            </div>
            <button onClick={onInsights} className="w-full py-2.5 rounded-control border border-hairline text-sm font-semibold text-ink">Show insights</button>
          </div>
        ) : (
          <div className="bg-surface border border-hairline rounded-card p-4 space-y-3">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-accent-ink shrink-0 mt-px" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-ink">Want a read on this game?</p>
                <p className="text-[13px] text-ink-2">
                  Dugout can go through the play-by-play and call out what stood out, inning by inning.
                </p>
              </div>
            </div>
            <button onClick={onInsights} className="w-full py-2.5 rounded-control border border-hairline text-sm font-semibold text-ink">Write AI insights</button>
          </div>
        )
      )}

      {insights && insights.length > 0 && groupInsightsByInning(insights).map((group, gi) => (
        <div key={gi} className="space-y-2">
          {group.label && <SectionLabel>{group.label}</SectionLabel>}
          {group.items.map((ins, i) => <GameInsightCard key={i} insight={ins} />)}
        </div>
      ))}

      <div className="space-y-2">
        <button onClick={onDone} className="w-full py-3.5 rounded-control bg-ink text-surface text-[15px] font-semibold">Done</button>
        {onViewBoxScore && (
          <button onClick={onViewBoxScore} className="w-full py-3 rounded-control border border-hairline bg-surface text-sm font-semibold text-ink">View box score</button>
        )}
      </div>
    </div>
  );
}
